import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';

import UpdateShippingDialogBox from './UpdateShippingDialogBox';
import BackorderDialogBox from './BackorderDialogBox';
import CancelDialogBox from './CancelDialogBox';
import CancelOrderDialogBox from './CancelOrderDialogBox';
import CancellationReasonBox from './CancellationReasonBox';

import NotFoundIcon from './../../assets/icons/not-found.png';
import { ArrowBackIcon, LinkIcon, CartIcon, ProfileIcon, ArrowDownIcon, CancelOrderIcon, ToastSuccessIcon } from './Styles/Icons';
import {
	FlexRow,
	FlexColumn,
	TitleSection,
	TitleRow,
	Title,
	RootContainer,
	NotFound,
	Popover,
	PopoverItem,
	PopoverTitle,
	PopoverBody,
	ShipmentLine,
	ShipmentDivider,
	ShipmentKey,
	ShipmentValue,
	ShipmentValueLight,
	ShipmentTrackingLink,
	ShipmentNote,
	OrderDetailsHeader,
	OrderId,
	OrderDate,
	OrderStats,
	OrderHeaderActions,
	CancelOrderButton,
	PartiallyCancelledPill,
	OrderCancelledPill,
	PartiallyFulfilledPill,
	OrderReturnedPill,
	Toast,
	TableContainer,
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableColumn,
	TableHeaderRow,
	TableHeaderColumn,
	ProductImage,
	OrderDetailsContainer,
	PaymentDetailsContainer,
	PaymentSummaryContainer,
	BackButton,
	ProductName,
	ProductId,
	ProductPrice,
	ProductQuantity,
	ProductStatus,
	ProductStatusColumn,
	ProductStatusShipBy,
	ProductStatusComment,
	PaymentDetailsHeader,
	PaymentLine,
	PaymentKey,
	PaymentValue,
	PaymentConclusion,
	CustomerDetailsContainer,
	CustomerDetailsHeader,
	CustomerImage,
	CustomerDetailsValue,
	OrderTrackingLink,
	MoreDetails,
	CustomerSummaryContainer,
	CustomerDetailsLine,
	CustomerDetailsKey,
	OrderNotesDetailsContainer,
	OrderNotesDetailsHeader,
	OrderNotesDetailsSubHeader,
	OrderNotesSummaryContainer,
} from './Styles/OrderDetails.jsx';

import { getOrderDetails, updateOrderLines } from '../../actions/ordersActions.js';
import { formatCurrency, getOrderMonth } from '../../commons/utils';
import { ShippingOptions, formatShipByDate, isCancellableLine, formatEstimatedDeliveryDate, isAllowedTrackingUrl } from './Utils';

const PopoverIds = {
	SHIPPING: 'shipping-popover',
	TRACKING: 'tracking-popover',
};
const DialogIds = {
	SHIPPING: 'shipping-dialog',
	BACKORDER: 'backorder-dialog',
	CANCEL: 'cancel-dialog',
};

class OrderDetails extends Component {
	constructor(props) {
		super(props);
		this.state = {
			itemsCount: 0,
			unfulfilledItemsCount: 0,
			shippingPopoverItem: {},
			shippingPopoverAnchorEl: null,
			trackingPopoverItem: null,
			trackingPopoverAnchorEl: null,
			updateShippingDialogOpen: false,
			backorderDialogOpen: false,
			cancelDialogOpen: false,
			cancelOrderDialogOpen: false,
			toastMessage: null,
		};
	}

	async componentDidMount() {
		await this.getOrderDetails();
	}

	componentWillUnmount() {
		clearTimeout(this.toastTimeout);
	}

	showToast = (message) => {
		clearTimeout(this.toastTimeout);
		this.setState({ toastMessage: message });
		this.toastTimeout = setTimeout(() => this.setState({ toastMessage: null }), 3200);
	};

	openCancelOrderDialog = () => {
		this.setState({ cancelOrderDialogOpen: true });
	};

	closeCancelOrderDialog = () => {
		this.setState({ cancelOrderDialogOpen: false });
	};

	cancelOrderLines = async (selectedIds, comment) => {
		const { selectedOrder, orderSummary, orderLines } = this.props;

		const openLinesCount = orderLines.filter(isCancellableLine).length;
		const stillOpen = openLinesCount - selectedIds.length;
		const itemWord = (n) => `${n} item${n === 1 ? '' : 's'}`;

		const body = {
			status: ShippingOptions.CANCELLED,
			comment,
			isBulkCancel: true,
			lineItemsList: selectedIds.map((orderLineId) => ({
				orderLineId,
				shippedQty: 0,
			})),
		};

		try {
			await this.props.updateOrderLines(selectedOrder._id, body, () => {});
			await this.getOrderDetails();
		} catch (err) {
			this.closeCancelOrderDialog();
			await this.getOrderDetails();
			return;
		}

		this.closeCancelOrderDialog();

		this.showToast(
			stillOpen > 0
				? `${itemWord(selectedIds.length)} cancelled · ${stillOpen} still open`
				: `Order ${orderSummary.order_id} cancelled · ${itemWord(selectedIds.length)}`,
		);
	};

	handlePopoverClick = (popoverId, event) => {
		switch (popoverId) {
			case PopoverIds.SHIPPING:
				this.setState({ shippingPopoverAnchorEl: event.currentTarget });
				break;

			default:
				break;
		}
	};

	handlePopoverSelect = (popoverId, choice) => {
		switch (popoverId) {
			case PopoverIds.SHIPPING:
				if (choice === ShippingOptions.FULFILLED) {
					this.setState({ updateShippingDialogOpen: true });
				} else if (choice === ShippingOptions.BACKORDER) {
					this.setState({ backorderDialogOpen: true });
				} else if (choice === ShippingOptions.CANCELLED) {
					this.setState({ cancelDialogOpen: true });
				} else {
					this.updateShippingDetails(choice);
				}
				break;

			default:
				break;
		}
		this.handlePopoverClose(popoverId);
	};

	handlePopoverClose = (popoverId) => {
		switch (popoverId) {
			case PopoverIds.SHIPPING:
				this.setState({ shippingPopoverAnchorEl: null });
				break;

			case PopoverIds.TRACKING:
				this.setState({ trackingPopoverAnchorEl: null, trackingPopoverItem: null });
				break;

			default:
				break;
		}
	};

	openTrackingPopover = (event, item) => {
		this.setState({ trackingPopoverAnchorEl: event.currentTarget, trackingPopoverItem: item });
	};

	handleDialogOnTrue = async (dialogId, value) => {
		switch (dialogId) {
			case DialogIds.SHIPPING:
				this.updateShippingDetails(ShippingOptions.FULFILLED, { trackingUrl: value });
				this.setState({ updateShippingDialogOpen: false });
				break;

			case DialogIds.BACKORDER:
				this.updateShippingDetails(ShippingOptions.BACKORDER, { expectedShipDate: value.expectedShipDate, comment: value.comment });
				this.setState({ backorderDialogOpen: false });
				break;

			case DialogIds.CANCEL:
				try {
					await this.updateShippingDetails(ShippingOptions.CANCELLED, { comment: value });
				} catch (err) {
					this.setState({ cancelDialogOpen: false });
					await this.getOrderDetails();
					break;
				}
				this.setState({ cancelDialogOpen: false });
				this.showToast('Item cancelled');
				break;

			default:
				break;
		}
	};

	handleDialogOnFalse = (dialogId) => {
		switch (dialogId) {
			case DialogIds.SHIPPING:
				this.setState({ updateShippingDialogOpen: false });
				break;

			case DialogIds.BACKORDER:
				this.setState({ backorderDialogOpen: false });
				break;

			case DialogIds.CANCEL:
				this.setState({ cancelDialogOpen: false });
				break;

			default:
				break;
		}
	};

	updateShippingDetails = async (status, payload = {}) => {
		const { shippingPopoverItem } = this.state;
		const { selectedOrder } = this.props;
		let body = {};

		switch (status) {
			case ShippingOptions.FULFILLED: {
				const trackingPayload = payload.trackingUrl;
				const isCustomCourierPayload = trackingPayload && typeof trackingPayload === 'object' && trackingPayload.isCustomCourier;

				body = {
					status: ShippingOptions.FULFILLED,
					trackingUrl: isCustomCourierPayload
						? {
								isCustomCourier: true,
								courierName: trackingPayload.courierName,
								value: trackingPayload.value,
								estimatedDelivery: trackingPayload.estimatedDelivery || null,
							}
						: trackingPayload || '',
					lineItemsList: [
						{
							orderLineId: shippingPopoverItem._id,
							shippedQty: shippingPopoverItem.qty,
						},
					],
				};

				if (isCustomCourierPayload && trackingPayload.estimatedDelivery) {
					body.estimatedDeliveryDate = trackingPayload.estimatedDelivery;
				}
				break;
			}

			case ShippingOptions.BACKORDER:
				body = {
					status: ShippingOptions.BACKORDER,
					expectedShipDate: payload.expectedShipDate,
					comment: payload.comment || '',
					lineItemsList: [
						{
							orderLineId: shippingPopoverItem._id,
							shippedQty: 0,
						},
					],
				};
				break;

			case ShippingOptions.CANCELLED:
				body = {
					status: ShippingOptions.CANCELLED,
					comment: payload.comment || '',
					lineItemsList: [
						{
							orderLineId: shippingPopoverItem._id,
							shippedQty: 0,
						},
					],
				};
				break;

			case ShippingOptions.PROCESSING:
			case ShippingOptions.RETURNED:
				body = {
					status: status,
					lineItemsList: [
						{
							orderLineId: shippingPopoverItem._id,
							shippedQty: 0,
						},
					],
				};
				break;

			default:
				break;
		}

		await this.props.updateOrderLines(selectedOrder._id, body, () => {});
		await this.getOrderDetails();
	};

	getOrderDetails = async () => {
		const { selectedOrder } = this.props;
		await this.props.getOrderDetails(selectedOrder._id);
		this.computeLineItems();
	};

	computeLineItems = () => {
		let { orderLines } = this.props;
		console.log('Here', orderLines);

		let fulfilled = 0;
		let totalItems = orderLines.length;

		orderLines.forEach((item) => {
			if (item.status === 'FULFILLED' || item.status === 'RETURNED') {
				fulfilled += 1;
			}
		});

		this.setState({
			unfulfilledItemsCount: totalItems - fulfilled,
			itemsCount: totalItems,
		});
	};

	render() {
		const { orderSummary = {}, orderLines = [], userDetails = {} } = this.props;
		const {
			itemsCount,
			unfulfilledItemsCount,
			updateShippingDialogOpen,
			backorderDialogOpen,
			cancelDialogOpen,
			cancelOrderDialogOpen,
			shippingPopoverAnchorEl,
			shippingPopoverItem,
			trackingPopoverAnchorEl,
			trackingPopoverItem,
			toastMessage,
		} = this.state;
		const isBackordered = shippingPopoverItem.status === ShippingOptions.BACKORDER;
		const canCancelOrder = orderLines.some(isCancellableLine);
		const cancelledCount = orderLines.filter((item) => item.status === ShippingOptions.CANCELLED).length;
		const isOrderCancelled = orderLines.length > 0 && cancelledCount === orderLines.length;
		const isPartiallyCancelled = !canCancelOrder && !isOrderCancelled && cancelledCount > 0
			&& orderLines.every((item) => item.status === ShippingOptions.CANCELLED || item.status === ShippingOptions.FULFILLED);
		const isPartiallyFulfilled = !canCancelOrder && !isOrderCancelled && !isPartiallyCancelled
			&& orderLines.some((item) => item.status === ShippingOptions.FULFILLED)
			&& orderLines.some((item) => [ShippingOptions.RETURNED, ShippingOptions.DENTIRAHOLD, ShippingOptions.CANCELLED].includes(item.status));
		const isOrderReturned = orderLines.length > 0 && orderLines.every((item) => item.status === ShippingOptions.RETURNED);

		const formattedDate = orderSummary.order_date ? new Date(orderSummary.order_date) : null;
		const day = formattedDate ? formattedDate.getDate() : null;
		const month = formattedDate ? getOrderMonth(formattedDate.getMonth()) : null;
		const year = formattedDate ? formattedDate.getFullYear() : null;
		const hours = formattedDate ? formattedDate.getHours() : null;
		const minutes = formattedDate ? formattedDate.getMinutes() : null;

		return (
			<RootContainer>
				<Helmet>
					<title>Seller Portal | Order Details</title>
				</Helmet>

				<Popover
					id={PopoverIds.SHIPPING}
					open={Boolean(shippingPopoverAnchorEl)}
					anchorEl={shippingPopoverAnchorEl}
					onClose={() => this.handlePopoverClose(PopoverIds.SHIPPING)}
					anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					{isBackordered ? (
						<>
							<PopoverItem
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.BACKORDER);
								}}
							>
								Update ETA
							</PopoverItem>

							<PopoverItem
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.FULFILLED);
								}}
							>
								Fulfill
							</PopoverItem>

							<PopoverItem
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.CANCELLED);
								}}
							>
								Cancel
							</PopoverItem>
						</>
					) : (
						<>
							<PopoverItem
								disabled={[ShippingOptions.PENDING, ShippingOptions.PROCESSING, ShippingOptions.FULFILLED, ShippingOptions.RETURNED, ShippingOptions.CANCELLED].includes(
									shippingPopoverItem.status,
								)}
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.PROCESSING);
								}}
							>
								Processing
							</PopoverItem>

							<PopoverItem
								disabled={[ShippingOptions.FULFILLED, ShippingOptions.RETURNED, ShippingOptions.CANCELLED].includes(shippingPopoverItem.status)}
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.BACKORDER);
								}}
							>
								Backordered
							</PopoverItem>

							<PopoverItem
								disabled={[ShippingOptions.FULFILLED, ShippingOptions.RETURNED, ShippingOptions.CANCELLED].includes(shippingPopoverItem.status)}
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.FULFILLED);
								}}
							>
								Fulfill
							</PopoverItem>

							<PopoverItem
								disabled={![ShippingOptions.FULFILLED].includes(shippingPopoverItem.status)}
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.RETURNED);
								}}
							>
								Return
							</PopoverItem>

							<PopoverItem
								disabled={[ShippingOptions.FULFILLED, ShippingOptions.RETURNED, ShippingOptions.CANCELLED].includes(shippingPopoverItem.status)}
								onClick={(e) => {
									this.handlePopoverSelect(PopoverIds.SHIPPING, ShippingOptions.CANCELLED);
								}}
							>
								Cancel
							</PopoverItem>
						</>
					)}
				</Popover>

				<Popover
					id={PopoverIds.TRACKING}
					open={Boolean(trackingPopoverAnchorEl)}
					anchorEl={trackingPopoverAnchorEl}
					onClose={() => this.handlePopoverClose(PopoverIds.TRACKING)}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				>
					{trackingPopoverItem ? (
						<>
							<PopoverTitle>{`Shipment ${trackingPopoverItem.index + 1}`}</PopoverTitle>

							<PopoverBody>
								<ShipmentLine>
									<ShipmentKey>Courier</ShipmentKey>
									<ShipmentValue>{trackingPopoverItem.courierName}</ShipmentValue>
								</ShipmentLine>

								{trackingPopoverItem.value ? (
									<ShipmentLine>
										<ShipmentKey>{isAllowedTrackingUrl(trackingPopoverItem.value) ? 'Tracking' : 'Reference'}</ShipmentKey>
										{isAllowedTrackingUrl(trackingPopoverItem.value) ? (
											<ShipmentTrackingLink
												href={trackingPopoverItem.value}
												target='_blank'
												rel='noopener noreferrer'
											>
												Track package ↗
											</ShipmentTrackingLink>
										) : (
											<ShipmentValue>{trackingPopoverItem.value}</ShipmentValue>
										)}
									</ShipmentLine>
								) : null}

								{trackingPopoverItem.estimatedDelivery ? (
									<ShipmentLine>
										<ShipmentKey>Estimated Delivery</ShipmentKey>
										<ShipmentValue>
											{formatEstimatedDeliveryDate(trackingPopoverItem.estimatedDelivery)}{' '}
											<ShipmentValueLight>(per supplier)</ShipmentValueLight>
										</ShipmentValue>
									</ShipmentLine>
								) : null}

								<ShipmentDivider />
								<ShipmentNote>Custom courier. These details are shown to the buyer.</ShipmentNote>
							</PopoverBody>
						</>
					) : null}
				</Popover>

				<UpdateShippingDialogBox
					open={updateShippingDialogOpen}
					onTrue={(value) => this.handleDialogOnTrue(DialogIds.SHIPPING, value)}
					onFalse={() => this.handleDialogOnFalse(DialogIds.SHIPPING)}
				/>

				<BackorderDialogBox
					open={backorderDialogOpen}
					isUpdate={isBackordered}
					initialComment={isBackordered ? shippingPopoverItem.comment : ''}
					initialExpectedShipDate={isBackordered ? shippingPopoverItem.expected_ship_date : null}
					onTrue={(value) => this.handleDialogOnTrue(DialogIds.BACKORDER, value)}
					onFalse={() => this.handleDialogOnFalse(DialogIds.BACKORDER)}
				/>

				<CancelDialogBox
					open={cancelDialogOpen}
					onTrue={(value) => this.handleDialogOnTrue(DialogIds.CANCEL, value)}
					onFalse={() => this.handleDialogOnFalse(DialogIds.CANCEL)}
				/>

				<CancelOrderDialogBox
					open={cancelOrderDialogOpen}
					orderId={orderSummary.order_id}
					orderLines={orderLines}
					onTrue={(selectedIds, comment) => this.cancelOrderLines(selectedIds, comment)}
					onFalse={() => this.closeCancelOrderDialog()}
				/>

				<TitleSection>
					<TitleRow responsive='1200px'>
						<FlexColumn style={{ display: 'inline' }}>
							<FlexRow style={{ gap: '2rem' }}>
								<Title>
									<BackButton
										outline
										onClick={() => this.props.onClose()}
									>
										<ArrowBackIcon />
									</BackButton>
									<span style={{ marginLeft: '1rem', fontSize: '16px', fontWeight: '500' }}>Back to all orders</span>
								</Title>
							</FlexRow>
						</FlexColumn>
					</TitleRow>
				</TitleSection>

				<FlexRow
					responsive={'900px'}
					style={{ padding: '0 2rem', margin: '0.5rem 0', gap: '2rem' }}
				>
					<FlexColumn style={{ flex: '9', gap: '2rem' }}>
						<FlexRow>
							<OrderDetailsContainer>
								<OrderDetailsHeader>
									<FlexRow>
										<OrderId>{orderSummary.order_id ? orderSummary.order_id : ''}</OrderId>
									</FlexRow>

									<FlexRow>
										<OrderDate>
											Ordered on {day} {month} {year} at {hours}:{minutes} PM
										</OrderDate>
									</FlexRow>

									<OrderHeaderActions>
										<OrderStats>{`Total: ${itemsCount ? itemsCount : 0} Unfulfilled: ${unfulfilledItemsCount ? unfulfilledItemsCount : 0}`}</OrderStats>

										{canCancelOrder ? (
											<CancelOrderButton onClick={() => this.openCancelOrderDialog()}>
												<CancelOrderIcon />
												Cancel Order
											</CancelOrderButton>
										) : isOrderCancelled ? (
											<OrderCancelledPill>Order Cancelled</OrderCancelledPill>
										) : isPartiallyCancelled ? (
											<PartiallyCancelledPill>Partially Cancelled</PartiallyCancelledPill>
										) : isPartiallyFulfilled ? (
											<PartiallyFulfilledPill>Partially Fulfilled</PartiallyFulfilledPill>
										) : isOrderReturned ? (
											<OrderReturnedPill>Order Returned</OrderReturnedPill>
										) : null}
									</OrderHeaderActions>
								</OrderDetailsHeader>

								<TableContainer orderNoteExists = {orderSummary?.order_notes?.trim()}>
									<Table>
										<TableHeader>
											<TableHeaderRow>
												<TableHeaderColumn style={{ width: '55%' }}>Product Details</TableHeaderColumn>

												<TableHeaderColumn style={{ width: '15%', justifyContent: 'left' }}>Price</TableHeaderColumn>

												<TableHeaderColumn style={{ width: '15%', justifyContent: 'center' }}>Qty</TableHeaderColumn>

												<TableHeaderColumn style={{ width: '15%' }}>Status</TableHeaderColumn>
											</TableHeaderRow>
										</TableHeader>

										<TableBody>
											{orderLines ? (
												orderLines.map((item, index) => (
													<React.Fragment key={index}>
													<TableRow>
														<TableColumn style={{ width: '55%' }}>
															<FlexRow>
																<FlexColumn style={{ width: '4rem' }}>
																	<ProductImage src={item.image_url ? item.image_url : NotFoundIcon} />
																</FlexColumn>

																<FlexColumn>
																	<ProductName>{item.name}</ProductName>
																	<ProductId>{`Item ID: ${item.supplier_product_id}`}</ProductId>
																</FlexColumn>
															</FlexRow>
														</TableColumn>

														<TableColumn style={{ width: '15%', justifyContent: 'left' }}>
															<ProductPrice>{formatCurrency(item.price)}</ProductPrice>
														</TableColumn>

														<TableColumn style={{ width: '15%', justifyContent: 'center' }}>
															<ProductQuantity>{`${item.qty}`}</ProductQuantity>
														</TableColumn>

														<TableColumn style={{ width: '15%' }}>
															<ProductStatusColumn
																onClick={(e) => {
																	this.setState({ shippingPopoverItem: item });
																	this.handlePopoverClick(PopoverIds.SHIPPING, e);
																}}
															>
																<ProductStatus>
																	{`${item.status || ''}`}
																	<ArrowDownIcon />
																</ProductStatus>

																{item.status === ShippingOptions.BACKORDER && item.expected_ship_date ? (
																	<ProductStatusShipBy>Ships by {formatShipByDate(item.expected_ship_date)}</ProductStatusShipBy>
																) : null}

																{item.status === ShippingOptions.BACKORDER && item.comment ? (
																	<ProductStatusComment>{item.comment}</ProductStatusComment>
																) : null}
															</ProductStatusColumn>
														</TableColumn>
													</TableRow>

													{item.status === ShippingOptions.CANCELLED && item.comment ? (
														<TableRow>
															<TableColumn
																colSpan={4}
																style={{ paddingTop: 0 }}
															>
																<CancellationReasonBox item={item} />
															</TableColumn>
														</TableRow>
													) : null}
													</React.Fragment>
												))
											) : (
												<TableRow key='not-found'>
													<TableColumn colSpan={'100%'}>
														<NotFound lines={["Couldn't find any items"]} />
													</TableColumn>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</OrderDetailsContainer>
						</FlexRow>

						{orderSummary?.order_notes?.trim() ? (
							<FlexRow>
								<OrderNotesDetailsContainer>
									<OrderNotesDetailsHeader>Order Note (Note from buyer)</OrderNotesDetailsHeader>
									<OrderNotesDetailsSubHeader>Review before fulfilling this order</OrderNotesDetailsSubHeader>

									<OrderNotesSummaryContainer>
										{orderSummary?.order_notes}
									</OrderNotesSummaryContainer>
								</OrderNotesDetailsContainer>
							</FlexRow>
						) : null}

						<FlexRow>
							<PaymentDetailsContainer>
								<PaymentDetailsHeader>Payment Summary</PaymentDetailsHeader>

								<PaymentSummaryContainer>
									<FlexRow>
										<FlexColumn>
											<PaymentLine>
												<PaymentKey> Subtotal </PaymentKey>
												<PaymentValue>{orderSummary.sub_total ? formatCurrency(orderSummary.sub_total) : 'NA'}</PaymentValue>
											</PaymentLine>
										</FlexColumn>

										<FlexColumn>
											<PaymentLine>
												<PaymentKey> Delivery Charges </PaymentKey>
												<PaymentValue>{orderSummary.shipping_charge ? formatCurrency(orderSummary.shipping_charge) : 'NA'}</PaymentValue>
											</PaymentLine>
										</FlexColumn>
									</FlexRow>

									<FlexRow>
										<FlexColumn>
											<PaymentLine>
												<PaymentKey> Taxes </PaymentKey>
												<PaymentValue>{orderSummary.tax ? formatCurrency(orderSummary.tax) : 'NA'}</PaymentValue>
											</PaymentLine>
										</FlexColumn>

										<FlexColumn>
											<PaymentConclusion>
												<PaymentKey> Paid by Customer </PaymentKey>
												<PaymentValue>{orderSummary.total ? formatCurrency(orderSummary.total) : 'NA'}</PaymentValue>
											</PaymentConclusion>
										</FlexColumn>
									</FlexRow>
								</PaymentSummaryContainer>
							</PaymentDetailsContainer>
						</FlexRow>
					</FlexColumn>

					<FlexColumn style={{ flex: '3', maxWidth: 'auto', minWidth: '250px' }}>
						<FlexRow>
							<CustomerDetailsContainer>
								<CustomerDetailsHeader>Customer Details</CustomerDetailsHeader>

								<CustomerSummaryContainer>
									<CustomerImage>
										<ProfileIcon />
									</CustomerImage>

									<CustomerDetailsLine>
										<CustomerDetailsKey>{userDetails.name ? userDetails.name : ''}</CustomerDetailsKey>

										<CustomerDetailsValue>{userDetails.email ? userDetails.email : ''}</CustomerDetailsValue>

										<CustomerDetailsValue>{userDetails.phone ? userDetails.phone : ''}</CustomerDetailsValue>
									</CustomerDetailsLine>

									<CustomerDetailsLine>
										<CustomerDetailsKey>Shipping Address</CustomerDetailsKey>
										<CustomerDetailsValue>
											{orderSummary?.shipping_address
												? Array.isArray(orderSummary.shipping_address)
													? orderSummary.shipping_address.join(', ')
													: orderSummary.shipping_address
												: userDetails.address
													? `${userDetails?.address?.line1 || ''}, ${userDetails?.address?.city || ''}, ${userDetails?.address?.state || ''}, ${
															userDetails?.address?.zip_code || ''
														}`
													: ''}
										</CustomerDetailsValue>
									</CustomerDetailsLine>

									{orderSummary?.ship_to ? (
										<CustomerDetailsLine>
											<CustomerDetailsKey>Ship To</CustomerDetailsKey>
											<CustomerDetailsValue>{orderSummary?.ship_to}</CustomerDetailsValue>
										</CustomerDetailsLine>
									) : null}

									{orderSummary.tracking_urls && orderSummary.tracking_urls.length ? (
										<CustomerDetailsLine>
											<CustomerDetailsKey>Tracking Links</CustomerDetailsKey>
											{orderSummary.tracking_urls.map((link, index) =>
												link && typeof link === 'object' && link.isCustomCourier ? (
													<OrderTrackingLink
														key={index}
														as='span'
														style={{ cursor: 'pointer' }}
														onClick={(e) => this.openTrackingPopover(e, { ...link, index })}
													>
														<LinkIcon /> {`Link ${index + 1}`}
													</OrderTrackingLink>
												) : (
													<OrderTrackingLink
														key={index}
														href={link}
														target='_blank'
													>
														<LinkIcon /> {`Link ${index + 1}`}
													</OrderTrackingLink>
												),
											)}
										</CustomerDetailsLine>
									) : null}
								</CustomerSummaryContainer>

								<MoreDetails>
									<FlexColumn>
										<CustomerDetailsKey> More Details </CustomerDetailsKey>
										<CustomerDetailsValue>{`Placed ${userDetails.orderCountByUser || 0} orders in total`}</CustomerDetailsValue>
									</FlexColumn>
									<FlexColumn style={{ alignSelf: 'center', width: '1.5rem' }}>
										<CartIcon />
									</FlexColumn>
								</MoreDetails>
							</CustomerDetailsContainer>
						</FlexRow>
					</FlexColumn>
				</FlexRow>

				{toastMessage ? (
					<Toast>
						<ToastSuccessIcon />
						{toastMessage}
					</Toast>
				) : null}
			</RootContainer>
		);
	}
}
const mapStateToProps = (state) => {
	let orderDetails = state.orders.order_details || {};
	return {
		orderSummary: orderDetails.orderSummary || {},
		orderLines: orderDetails.orderLines || [],
		userDetails: orderDetails.userDetails || {},
	};
};

const mapDispatchToProps = () => {
	return {
		getOrderDetails,
		updateOrderLines,
	};
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps())(OrderDetails));
